import {
  type Template,
  TemplateStatus,
  type UpdateTemplateRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useUpdateTemplateMutation } from '../../graphql/hooks/useUpdateTemplateMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { TemplateForm } from './TemplateForm';

type FormValues = UpdateTemplateRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().nullish(),
  labelIds: z.array(z.string()).nullish(),
  status: z.enum(TemplateStatus).nullish(),
});

interface Props {
  template: Template;
}

export const TemplateEditModal = createModal<Props>((props) => {
  const { template, modal } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, visible, remove } = modal;

  const initialValues: Partial<FormValues> = {
    name: template.name,
    labelIds: template.labels.map((label) => label.id),
    status: template.status,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateTemplateMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          templateId: template.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Template updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating template: ${err.message}`);
        });
    },
    [mutation, organization.id, template.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a template'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <TemplateForm isEditing />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
